/*
  Noma product page: "More parts from this car" and "Similar parts" (same OE/OEM part number).
  Loaded with `defer` by sections/noma-car-parts.liquid and sections/noma-similar-parts.liquid (possibly twice: everything is idempotent).
  Without this file the page is complete: the car link is a plain <a> and the similar-parts section stays hidden.

  How it works (the storefront has no "products with this part number" object, so the search is used):
  * the section renders a <noma-related> element with data attributes (current product handle, car label or raw OE/OEM text);
  * the element fetches  /search?q=...&section_id=<results section>  (the section rendering API on the search URL), which prints one
    <li class="noma-rel__item"> card per search result (sections/noma-car-parts-results.liquid, noma-similar-results.liquid);
  * the cards are parsed with DOMParser (never innerHTML of the live page), filtered here (not the current product, in stock, same car /
    same part number) and the first few are inserted. On any failure the section falls back to its quiet state, it never shows an error.
  * when it loads: the car row when it is 700px from the screen (it keeps its height with placeholders meanwhile); the similar parts as soon
    as the browser is idle after the page has loaded. They sit above the footer: a section that arrives while the shopper is already at the
    bottom (End key, a fast flick, the browser restoring the scroll position) pushes the footer away, which measured 0.07 to 0.5 layout shift.
    With the data saver on they wait until they are near the screen instead.

  Part numbers are compared as normalised text, written exactly one way here (partNumbers): "T2H26342", "T2H 26342" and "T2H-26342" are
  one number. Liquid only prints the raw text (data-oem), so the current product and the candidates can never be normalised differently.
  Only the item specific "OE/OEM Part Number" (and the SEO primary number) is used, never noma.oem_numbers: on listings written by the
  first sync mapping that list also holds chassis codes (L405, L494 ...) and year-like tokens that thousands of listings share.
*/
(function () {
  'use strict';

  // ------------------------------------------------------------------ part numbers (pure functions, also used by the harness tests)

  var JUNK = /^(oem|n\/?a|na|none|unknown|does not apply|see description|genuine|original|used|part|number)$/i;

  /** "T2H 26342" -> "T2H26342": upper case, letters and digits only. */
  function compact(text) {
    return String(text == null ? '' : text)
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
  }

  /** The compact form of one token when it can be a part number, else ''. */
  function numberOf(token) {
    var raw = String(token).trim().replace(/[.,;:]+$/, '');
    var k = compact(raw);
    // a number written twice without a separator ("MJE-2540-ABMJE2540AB"): keep one copy
    if (k.length >= 8 && k.length % 2 === 0 && k.slice(0, k.length / 2) === k.slice(k.length / 2)) k = k.slice(0, k.length / 2);
    if (k.length < 4 || k.length > 22) return '';
    if (!/\d/.test(k)) return '';
    if (JUNK.test(raw)) return '';
    if (/^(19|20)\d\d$/.test(k)) return '';
    if (/^\d{1,3}K$/.test(k)) return '';
    if (!/^[A-Za-z0-9][A-Za-z0-9 ._#-]*$/.test(raw)) return '';
    return k;
  }

  /** "LR070364 LR0 70364" (a missing slash) -> two tokens; "LR098915 LR084050" (numbers separated by spaces) -> two tokens. */
  function unglue(piece) {
    var words = piece.split(/\s+/);
    if (words.length >= 2 && compact(words.slice(1).join('')) === compact(words[0])) return [words[0], words.slice(1).join(' ')];
    if (
      words.length >= 2 &&
      words.every(function (w) {
        return compact(w).length >= 5 && /\d/.test(w);
      })
    )
      return words;
    return [piece];
  }

  /**
   * Raw OE/OEM text ("T2H26342 / T2H 26342 / T2H-26342 / C2D43874 ...", slash, comma, semicolon, pipe or line separated) -> the distinct
   * normalised numbers in the order they are written.
   */
  function partNumbers(text) {
    var out = [];
    String(text == null ? '' : text)
      .split(/\s*[\/,;|\r\n]+\s*/)
      .forEach(function (piece) {
        piece = piece.trim();
        if (!piece) return;
        unglue(piece).forEach(function (token) {
          var k = numberOf(token);
          if (k && out.indexOf(k) === -1) out.push(k);
        });
      });
    return out;
  }

  /** How many entries of `list` are in `set` (an object used as a set). */
  function overlap(list, set) {
    var n = 0;
    for (var i = 0; i < list.length; i++) if (set[list[i]]) n++;
    return n;
  }

  window.NomaPartNumbers = window.NomaPartNumbers || { compact: compact, partNumbers: partNumbers, overlap: overlap };

  if (!window.customElements || !window.fetch || !window.DOMParser) return;

  // ------------------------------------------------------------------ the element

  var TIMEOUT_MS = 6000;
  var MAX_QUERIES = 3;

  function attrInt(el, name, fallback, min, max) {
    var n = parseInt(el.getAttribute(name), 10);
    if (isNaN(n)) n = fallback;
    return Math.max(min, Math.min(max, n));
  }

  /**
   * The `li.noma-rel__item` nodes of a section rendering API response and the number of results the search found (data-total of the list,
   * NaN when the section did not print it), or null when the request failed or the answer is not our section.
   */
  function fetchItems(url, signal) {
    return fetch(url, { signal: signal, credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then(function (res) {
        if (!res.ok) return null;
        return res.text();
      })
      .then(function (html) {
        if (html == null) return null;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var list = doc.querySelector('ul.noma-rel__list');
        // not our results section (an error page, an empty answer): a failure, not "no results"
        if (!list) return null;
        return {
          items: Array.prototype.slice.call(doc.querySelectorAll('li.noma-rel__item')),
          total: parseInt(list.getAttribute('data-total'), 10),
        };
      })
      .catch(function () {
        return null;
      });
  }

  /**
   * Runs `fn` once the page has loaded and the browser has a free moment (a short timer where requestIdleCallback does not exist); a
   * page whose load event is held up by a slow third-party file does not keep the shopper waiting for ever: 4 s at the latest.
   */
  function whenIdle(fn) {
    var started = false;
    function run() {
      if (started) return;
      started = true;
      if (window.requestIdleCallback) window.requestIdleCallback(fn, { timeout: 2000 });
      else setTimeout(fn, 600);
    }
    if (document.readyState === 'complete') run();
    else {
      window.addEventListener('load', run, { once: true });
      setTimeout(run, 4000);
    }
  }

  function saveData() {
    var c = navigator.connection;
    return !!(c && c.saveData);
  }

  function isAvailable(item) {
    return item.getAttribute('data-available') !== 'false';
  }

  if (!customElements.get('noma-related')) {
    customElements.define(
      'noma-related',
      class NomaRelated extends HTMLElement {
        connectedCallback() {
          if (!this.nomaReady) {
            this.nomaReady = true;
            this.kind = this.getAttribute('data-kind') === 'similar' ? 'similar' : 'car';
            this.grid = this.querySelector('[data-noma-rel-grid]');
            this.handle = this.getAttribute('data-handle') || '';
            this.max = attrInt(this, 'data-max', this.kind === 'car' ? 4 : 8, 1, 12);
            if (!this.grid || !this.getUrls().length) {
              this.setState('empty');
              return;
            }
          }
          // moved in the theme editor before it loaded: start again, otherwise do nothing
          if (this.getAttribute('data-state') !== 'pending') return;
          // similar parts: as soon as the page is idle (see the header comment); everything else, and the data saver: when near the screen
          if (this.kind === 'similar' && !saveData()) {
            whenIdle(
              function () {
                if (this.isConnected && this.getAttribute('data-state') === 'pending') this.load();
              }.bind(this)
            );
          } else if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(
              function (entries) {
                if (!entries.some(function (e) { return e.isIntersecting; })) return;
                this.observer.disconnect();
                this.load();
              }.bind(this),
              { rootMargin: '700px 0px' }
            );
            this.observer.observe(this);
          } else {
            this.load();
          }
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
          if (this.controller) this.controller.abort();
          this.controller = null;
          clearTimeout(this.timer);
          if (this.getAttribute('data-state') === 'loading') this.setAttribute('data-state', 'pending'); // an aborted request is not an answer
        }

        setState(state) {
          this.setAttribute('data-state', state);
          this.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
        }

        /** The section rendering API URLs to read: one for the car, up to MAX_QUERIES (one per part number) for similar parts. */
        getUrls() {
          if (this.kind === 'car') {
            var u = this.getAttribute('data-fetch');
            return u ? [u] : [];
          }
          var base = this.getAttribute('data-search-url') || '/search';
          var section = this.getAttribute('data-section') || 'noma-similar-results';
          this.numbers = partNumbers(this.getAttribute('data-oem'));
          this.numberSet = {};
          this.numbers.forEach(function (n) { this.numberSet[n] = true; }, this);
          return this.numbers.slice(0, MAX_QUERIES).map(function (n) {
            return base + '?q=' + encodeURIComponent(n) + '&type=product&options%5Bprefix%5D=none&section_id=' + encodeURIComponent(section);
          });
        }

        load() {
          this.setState('loading');
          var controller = new AbortController();
          var timer = setTimeout(controller.abort.bind(controller), TIMEOUT_MS);
          this.controller = controller;
          this.timer = timer;
          Promise.all(this.getUrls().map(function (url) { return fetchItems(url, controller.signal); }))
            .then(function (lists) {
              clearTimeout(timer);
              // removed or moved while the request ran (the theme editor): the answer of a later load() counts, not this one
              if (this.controller !== controller || !this.isConnected) return;
              this.controller = null;
              this.show(lists);
            }.bind(this))
            .catch(function () {
              clearTimeout(timer);
              if (this.controller !== controller) return;
              this.controller = null;
              this.setState('error');
            }.bind(this));
        }

        show(lists) {
          var failed = lists.every(function (l) { return l === null; });
          if (failed) {
            this.setState('error'); // the car link stays, similar parts stay hidden
            return;
          }
          var seen = {};
          var items = [];
          var total = 0;
          lists.forEach(function (list) {
            if (!list) return;
            total = Math.max(total, list.total); // NaN stays NaN: the section did not say
            list.items.forEach(function (item) {
              var handle = item.getAttribute('data-handle');
              if (!handle || handle === this.handle || seen[handle]) return;
              seen[handle] = true;
              items.push(item);
            }, this);
          }, this);
          return this.kind === 'car' ? this.showCar(items, total) : this.showSimilar(items);
        }

        showCar(items, total) {
          var label = (this.getAttribute('data-label') || '').toUpperCase();
          var sameCar = items.filter(function (item) {
            var car = (item.getAttribute('data-car') || '').toUpperCase();
            return !label || !car || car === label;
          });
          if (!sameCar.length) {
            // The search found only this listing (or nothing): there is no other part of the car, so a link "More parts" would be false and
            // the row goes away. The car search is the exact SKU field query (variants.sku:<label>*), so its count is the number of parts of
            // the car and this rule is right. It found more than that but none of it is of this car (the section printed nothing, or it did
            // not say how many): the car may still have parts, the link stays.
            this.setState(total <= 1 ? 'empty' : 'error');
            return;
          }
          this.fill(sameCar.filter(isAvailable));
        }

        showSimilar(items) {
          var set = this.numberSet;
          var ranked = [];
          items.forEach(function (item, index) {
            if (!isAvailable(item)) return;
            var shared = overlap(partNumbers(item.getAttribute('data-oem')), set);
            if (shared) ranked.push({ item: item, shared: shared, index: index });
          });
          ranked.sort(function (a, b) { return b.shared - a.shared || a.index - b.index; });
          var shown = ranked.slice(0, this.max).map(function (r) { return r.item; });
          if (!shown.length) {
            this.setState('empty');
            return;
          }
          // "Same OE/OEM part number: LR049905" only when there is exactly one number to name
          var named = this.querySelector('[data-noma-rel-number]');
          if (named && this.numbers.length === 1) named.textContent = ': ' + this.numbers[0];
          this.fill(shown);
        }

        fill(items) {
          var cards = items.slice(0, this.max);
          if (!cards.length) {
            this.setState('error'); // there are other parts but none in stock: only the link
            return;
          }
          cards.forEach(function (item) {
            this.grid.appendChild(document.importNode(item, true));
          }, this);
          this.setState('ready');
        }
      }
    );
  }
})();
